// Libs
import React, { useMemo } from 'react';
import { Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useSelector } from 'react-redux';

// Components
import IconButton from '../../../components/Button/IconButton/IconButton';
import ReorderButton from '../../../components/Button/ReorderButton/ReorderButton';
import Feather from '../../../components/Feather';

// Hooks
import useSharedKitActions from './useSharedKitActions';
import useSharedKitOrder from './useSharedKitOrder';
import useUser from '../../../hooks/useUser';

// Utils
import { getTotalQty } from '../utils/calculations';

// Selectors
import { productsObjSelector } from '../../../store/selectors';

// Constants
import { ORDER_BUTTON_TITLE } from '../../../constants/sharedKits';

// Styles
import { useStyles } from './useSharedKitsCellsReducersStyles';

const useSharedKitsCellsReducers = ({ kit }) => {
  const { onViewDetailsClick, onDeleteClick, canOrder } =
    useSharedKitActions(kit);
  const { onOrderClick } = useSharedKitOrder();
  const { isKiteUser } = useUser();
  const { classes } = useStyles();
  const productsObj = useSelector((state) => productsObjSelector(state));

  const cells = useMemo(
    () => ({
      kitNickname: (
        <Typography
          data-testid={'shared-kit-name'}
          variant={isKiteUser ? 'textSm' : 'textSmSemibold'}
          className={classes.kitName}
          title={kit.name}>
          {kit.name}
        </Typography>
      ),
      totalQnty: getTotalQty(kit, productsObj),
      viewDetailsButton: (
        <IconButton
          data-testid={'shared-kit-details-button'}
          onClick={() => onViewDetailsClick(kit.id)}
          endIcon={<ChevronRightIcon className={classes.detailsIcon} />}
          className={classes.flatIconButton}>
          View Details
        </IconButton>
      ),
      orderButton: (
        <ReorderButton
          disabled={!canOrder}
          kit={kit}
          onClick={onOrderClick}
          title={ORDER_BUTTON_TITLE}
          inTable={true}
        />
      ),
      deleteButton: kit.is_owner ? (
        <span className={classes.removeIcon}>
          <Feather
            data-testid="shared-kit-remove-button"
            type="close"
            colorType="grey2"
            onClick={() => onDeleteClick(kit.id)}
          />
        </span>
      ) : null,
    }),
    [
      kit,
      onDeleteClick,
      onOrderClick,
      onViewDetailsClick,
      productsObj,
      canOrder,
      isKiteUser,
      classes,
    ],
  );

  return {
    cells,
  };
};

export default useSharedKitsCellsReducers;
